/**
 * DocumentUploadModal Component
 * Modal for file selection and upload
 */

import { useState, useRef, useEffect } from 'react';
import { activate } from "../../../../../utils/keyboard-activate";
import { useDocumentUpload } from '../../../../../hooks/useDocumentUpload';
import {
  validateDocumentFile,
  getAcceptedFileTypes,
} from '../../../../../utils/document-validation';
import { getFileNameWithoutExtension } from '../../../../../utils/document-utils';
import { ErrorBoundary } from '../../../../../components/ErrorBoundary';
import { ModalCloseButton } from '../../../../../components/ModalCloseButton';
import { GenericFileIcon } from '../../../../../assets/GenericFileIcon';
import './DocumentUploadModal.css';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  prospectId: string;
}

export function DocumentUploadModal({
  isOpen,
  onClose,
  onUploadComplete,
  prospectId,
}: DocumentUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadSuccessRefreshError, setUploadSuccessRefreshError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadDocument, isUploading, progress } = useDocumentUpload();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setTitle('');
      setValidationError(null);
      setUploadSuccessRefreshError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidationError(null);

    // Validate file
    const validation = validateDocumentFile(file);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid file');
      setSelectedFile(null);
      setTitle('');
      return;
    }

    // Set file and auto-fill title
    setSelectedFile(file);
    setTitle(getFileNameWithoutExtension(file.name));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadSuccessRefreshError(null);
    const result = await uploadDocument(prospectId, selectedFile, title.trim() || undefined);

    if (result.success) {
      try {
        await onUploadComplete();
        onClose();
      } catch (err) {
        console.error('[DocumentUploadModal] Upload succeeded but refresh failed', {
          error: err,
          prospectId,
          mediaId: result.mediaId,
        });
        setUploadSuccessRefreshError(
          'Document uploaded successfully! However, the list failed to refresh. Please close this dialog and refresh the page to see your document.'
        );
        // Don't close modal so user can see the message
      }
    } else {
      setValidationError(result.error || 'Upload failed. Please try again.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isUploading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ErrorBoundary
      fallbackTitle="Upload Error"
      fallbackMessage="Something went wrong during the upload process. Please close this dialog and try again."
      onReset={() => {
        setValidationError(null);
        setUploadSuccessRefreshError(null);
      }}
    >
      <div className="document-upload-modal__overlay" onClick={handleOverlayClick}>
        <div className="document-upload-modal__content">
        <div className="document-upload-modal__header">
          <h3 className="document-upload-modal__title">Add Document</h3>
          {!isUploading && <ModalCloseButton onClick={onClose} />}
        </div>

        <div className="document-upload-modal__body">
          {/* File Input */}
          <div className="document-upload-modal__form-group">
            <label htmlFor="document-file-input" className="document-upload-modal__label">
              File
            </label>
            <div className="document-upload-modal__file-input-wrapper">
              <input
                ref={fileInputRef}
                id="document-file-input"
                type="file"
                accept={getAcceptedFileTypes()}
                onChange={handleFileSelect}
                disabled={isUploading}
                className="document-upload-modal__file-input-hidden"
              />
              <div
                className={`document-upload-modal__file-input-display ${
                  selectedFile ? "document-upload-modal__file-input-display--selected" : ""
                }`}
                {...activate(() => !isUploading && fileInputRef.current?.click())}
              >
                {!selectedFile && (
                  <GenericFileIcon className="document-upload-modal__file-icon" />
                )}
                <span>
                  {selectedFile ? selectedFile.name : 'Choose File'}
                </span>
              </div>
            </div>
          </div>

          {/* Title Input */}
          <div className="document-upload-modal__form-group">
            <label htmlFor="document-title-input" className="document-upload-modal__label">
              Title (optional)
            </label>
            <input
              id="document-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="Enter a title for this document"
              disabled={!selectedFile || isUploading}
              className="document-upload-modal__text-input"
            />
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="document-upload-modal__error">
              {validationError}
            </div>
          )}

          {/* Upload Success with Refresh Warning */}
          {uploadSuccessRefreshError && (
            <div className="document-upload-modal__success-warning">
              <p>{uploadSuccessRefreshError}</p>
              <button
                onClick={onClose}
                className="document-upload-modal__warning-button"
              >
                Close
              </button>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="document-upload-modal__progress">
              <p className="document-upload-modal__progress-message">{progress.message}</p>
              <div className="document-upload-modal__progress-bar-container">
                <div
                  className="document-upload-modal__progress-bar"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <p className="document-upload-modal__progress-percent">{progress.percent}%</p>
            </div>
          )}
        </div>

        <div className="document-upload-modal__footer">
          <button
            className={`document-upload-modal__upload-button ${
              selectedFile && !isUploading
                ? "document-upload-modal__upload-button--ready"
                : ""
            }`}
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
    </ErrorBoundary>
  );
}
